import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash-es';
import { HTTPError } from 'ky';
import { api } from 'lib/request/api';
import type { WellStatusRow } from 'components/well-cards';
import {
  canEditFirstProductionDate,
  validateStatusEdit,
  type StatusEditError,
  type StatusEditValues,
} from './well-status-validation';
import type { InlineRow } from './detail/inline-field';
import type {
  BottomHoleOption,
  LtdEndKey,
  WellboreBody,
  WellboreSurfaceBody,
} from './detail/wellbore/composite-geometry';
import { FIELD_TO_SERVER_KEY } from 'types/well-workspace';
import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
  queryOptions,
} from '@tanstack/react-query';
import type { WellIntersectionAnalysisResponse } from 'types/api-responses';
import type {
  EditableDateField,
  ManualWellInput,
  StagedWell,
  WellQueryRow,
  WellStatusToken,
  WellWorkspaceDetail,
  WellWorkspaceListItem,
  WellsQueryResponse,
} from 'types/well-workspace';
import {
  type BoreChange,
  type CreateWorkspaceInput,
  type ListQuery,
  type WellIdentityEdit,
  type WellStatusEdit,
  type WellboreBuilt,
  type WellborePayload,
  type WorkspaceLink,
  deleteGeometrySourceSelection,
  deleteWellbore,
  fetchOperatorOptions,
  getCompare,
  getLtdCandidates,
  getWellUnitCandidates,
  getWellWorkspaceLinks,
  getWellboreGeometries,
  getWells,
  getWellsSummary,
  getWorkspace,
  getWorkspaces,
  getWorkspacesSummary,
  linkRow,
  deleteWell,
  patchPrimaryWellbore,
  patchWellIdentity,
  patchWellStatus,
  patchWellbore,
  postGeometrySourceSelection,
  postPromotion,
  postUnitAssignment,
  postWells,
  postWellStatusVerdict,
  postWellbore,
  postWorkspace,
} from './api';
import type { LtdCandidatesQuery } from './detail/wellbore/ltd-candidates';
import {
  ltdCandidateKeys,
  wellboreGeometryKeys,
  wellUnitCandidateKeys,
  wellWorkspaceKeys,
  wellsKeys,
} from './query-keys';
import {
  readWriteFailure,
  UNEXPLAINED_FAILURE,
  WorkspaceRefused,
} from './utils';

// React Query owns loading, caching, cancellation and retries here. Reads pass their
// AbortSignal to ky so a superseded read cancels itself; writes invalidate the keys
// they affect on success, so no screen refetches by hand.

// A read that fails is worth retrying once — a write is not, and a refusal is an answer
// rather than a blip.
const READ_RETRY = 1;

// ── Reads ──

export const useWorkspaces = (query: ListQuery) =>
  useQuery(
    queryOptions({
      queryKey: wellWorkspaceKeys.list(query),
      queryFn: ({ signal }) => getWorkspaces(query, signal),
      retry: READ_RETRY,
      // A page, search or sort change is a new key; serving the previous result keeps the
      // table mounted instead of blanking it while the next one loads.
      placeholderData: keepPreviousData,
      select: (
        data
      ): { rows: WellWorkspaceListItem[]; totalCount: number } => ({
        rows: data.results,
        totalCount: data.totalCount,
      }),
    })
  );

/**
 * The well workspaces one well belongs to, for the Well page's Workspaces drawer, the
 * View menu's counts and the promoted banner.
 *
 * Reads the endpoint's `all` list, falling back to `latest` plus `latestPromoted` on a
 * backend predating it. Those two are often the same row, hence the dedupe, and
 * `complete` tells the drawer whether to caveat the list.
 * @param {string} uwi The well.
 * @return {object} The query: the workspaces, the promoted one, and whether all are known.
 */
export const useWellWorkspaces = (uwi: string | undefined) =>
  useQuery(
    queryOptions({
      queryKey: wellWorkspaceKeys.linksFor(uwi ?? ''),
      queryFn: ({ signal }) => getWellWorkspaceLinks([uwi!], signal),
      enabled: Boolean(uwi),
      retry: READ_RETRY,
      select: (results) => {
        const payload = uwi ? results[uwi] : undefined;
        const promoted = linkRow(payload?.latestPromoted);

        const complete = Array.isArray(payload?.all);
        const rows = complete
          ? (payload?.all ?? []).map(linkRow)
          : [linkRow(payload?.latest), promoted];
        const known = rows.filter((row): row is WorkspaceLink => row !== null);

        return {
          promoted,
          // Every workspace this well was promoted from, for the banner's list. Derived
          // from the same rows so the banner and the drawer cannot disagree.
          promotedFrom: known.filter((row) => row.promoted),
          complete,
          workspaces: known.filter(
            (row, index) => known.findIndex((r) => r.id === row.id) === index
          ),
        };
      },
    })
  );

export const useWorkspacesSummary = () =>
  useQuery(
    queryOptions({
      queryKey: wellWorkspaceKeys.summary(),
      queryFn: ({ signal }) => getWorkspacesSummary(signal),
      retry: READ_RETRY,
    })
  );

export const useWorkspace = (id: string | number | undefined) =>
  useQuery(
    queryOptions({
      queryKey: wellWorkspaceKeys.detail(id ?? ''),
      queryFn: ({ signal }) => getWorkspace(id!, signal),
      // The route param is a string from react-router; an absent id means the shell is
      // still resolving, so hold the query rather than requesting `/workspaces/undefined`.
      enabled: id !== undefined && id !== '',
      retry: READ_RETRY,
    })
  );

// Compare, only while the sheet is open — it is a second read and nothing needs it
// until the user asks. Kept out of the detail query so opening Compare never delays
// the page.
export const useCompare = (id: string | number, enabled: boolean) =>
  useQuery(
    queryOptions({
      queryKey: wellWorkspaceKeys.compare(id),
      queryFn: ({ signal }) => getCompare(id, signal),
      enabled,
      retry: READ_RETRY,
    })
  );

export const useWellsSummary = () =>
  useQuery(
    queryOptions({
      queryKey: wellsKeys.summary(),
      queryFn: ({ signal }) => getWellsSummary(signal),
      retry: READ_RETRY,
    })
  );

// The wells listing for a search payload. Pass enabled=false to hold the request (e.g.
// while a modal is closed).
export const useWells = (payload: Record<string, unknown>, enabled = true) =>
  useQuery(
    queryOptions({
      queryKey: wellsKeys.query(payload),
      queryFn: ({ signal }) => getWells(payload, signal),
      enabled,
      retry: READ_RETRY,
      // Changing a filter is a new key, so without this the table would blank while the
      // next page loads. Serving the previous rows keeps the table mounted instead.
      placeholderData: keepPreviousData,
      select: (
        data
      ): {
        rows: WellQueryRow[];
        totalCount: number;
        filteredUwis?: WellsQueryResponse['filteredUwis'];
      } => ({
        rows: data.results,
        totalCount: data.totalCount,
        filteredUwis: data.filteredUwis,
      }),
    })
  );

// ── Writes ──

export interface CreateWorkspace {
  /** True while the create is in flight, to hold the modal's button. */
  saving: boolean;
  /** Why the last attempt was refused, or null. */
  error: string | null;
  create: (input: CreateWorkspaceInput) => void;
  reset: () => void;
}

// Creates a well workspace. On success the listing, the summary and any open detail are
// invalidated, so nothing has to refetch by hand. onCreated receives the new workspace,
// for the caller to navigate to it.
export const useCreateWorkspace = (
  onCreated: (workspace: WellWorkspaceDetail) => void
): CreateWorkspace => {
  const queryClient = useQueryClient();
  const { mutate, isPending, error, reset } = useMutation<
    WellWorkspaceDetail,
    Error,
    CreateWorkspaceInput
  >({
    mutationFn: postWorkspace,
    retry: false,
    onSuccess: (workspace) => {
      void queryClient.invalidateQueries({ queryKey: wellWorkspaceKeys.all });
      onCreated(workspace);
    },
  });

  return {
    saving: isPending,
    error: readWriteFailure(
      error,
      `Could not create the workspace. ${UNEXPLAINED_FAILURE}`
    ),
    create: mutate,
    reset,
  };
};

/** One Manage-Wells Save: the staged adds, as one batch, and the staged removals. */
export interface ManageWellsChanges {
  /** Existing wells to add, by uwi. */
  existingUwis: string[];
  /** New manual wells to add. */
  manual: ManualWellInput[];
  /** Members to remove, by uwi (or staged-well id — the endpoint resolves either). */
  removeUwis: string[];
}

/**
 * What one Save actually achieved. The adds are a single all-or-nothing batch, but the removals
 * are separate calls, so a Save can half-succeed and the sheet has to be able to say so.
 */
export interface ManageWellsOutcome {
  /** Existing-well uwis asked for. */
  requestedAdds: number;
  /** Uwis the backend dropped because they resolved to no live well. */
  skippedUwis: string[];
  /** Removals that went through, including ones already gone (a 404 is the asked-for state). */
  removed: string[];
  /** Removals that were refused, still staged so the reader can retry them. */
  failedRemovals: string[];
}

export interface WellsManager {
  /** True while the Save is in flight, to hold the sheet's button. */
  saving: boolean;
  /** Why the last Save was refused, in the backend's words, or null. */
  error: string | null;
  /** The refusal's code, for routing WORKSPACE_PROMOTED away from the inline banner. */
  errorCode: string | null;
  /**
   * Which manual-add row the last refusal named, or null. An index into the array that was
   * SENT, so the caller has to resolve it against what it sent rather than its own row list.
   */
  errorRow: number | null;
  save: (
    changes: ManageWellsChanges,
    callbacks?: {
      onSaved?: (outcome: ManageWellsOutcome) => void;
      /**
       * `addsLanded` as of THIS failure. Passed rather than read off the hook's return,
       * which is a render-time snapshot: a second Save in the same render cycle would hand
       * the caller the previous attempt's answer and clear the wrong half of its staged work.
       */
      onError?: (addsLanded: boolean) => void;
    }
  ) => void;
  reset: () => void;
}

/**
 * Commits one Manage-Wells session (Epic 3, Story 3): the staged adds go in a single batch POST,
 * then each staged removal is its own DELETE (the endpoint takes one well at a time).
 *
 * Adds run BEFORE removes so swapping one well for another never dips the workspace below its
 * last well mid-Save, and the removals run sequentially rather than in parallel because each is
 * its own transaction taking its own row lock on the workspace — firing them at once would have
 * them queue on that lock anyway, and a failure would be harder to attribute.
 * @param {string | number} id The workspace.
 * @return {WellsManager} The Save, its in-flight flag and its refusal.
 */
export const useManageWells = (id: string | number): WellsManager => {
  const queryClient = useQueryClient();
  /* Whether the add batch was accepted, read by the caller to decide how much of its staged
     work to throw away. A ref rather than state: it is written inside the mutation and read in
     the same turn by the save callbacks, and re-rendering on it would serve nothing. */
  const addsLandedRef = useRef(false);
  const { mutate, isPending, error, reset } = useMutation<
    ManageWellsOutcome,
    Error,
    ManageWellsChanges
  >({
    mutationFn: async ({ existingUwis, manual, removeUwis }) => {
      addsLandedRef.current = false;
      let skippedUwis: string[] = [];
      if (existingUwis.length > 0 || manual.length > 0) {
        // Throws on refusal: nothing landed, and the caller keeps its whole staged set.
        // `= []` on the pattern, not just on the declaration above: destructuring assigns
        // `undefined` when the key is absent, which would overwrite the default and leave the
        // sheet reading `.length` off nothing. The partial is documented to always send it,
        // but a response that does not is a missing banner, not a crash.
        ({ skippedUwis = [] } = await postWells(id, { existingUwis, manual }));
      }
      addsLandedRef.current = true;

      const removed: string[] = [];
      const failedRemovals: string[] = [];
      for (const uwi of removeUwis) {
        try {
          /* Sequential on purpose: each removal takes its own row lock on the workspace, so
             firing them at once would queue on that lock anyway and make a failure harder to
             attribute. */
          await deleteWell(id, uwi);
          removed.push(uwi);
        } catch (failure) {
          /* A 404 is the state the reader asked for — the well is already out, removed in
             another session or by someone else. Count it as removed rather than reporting a
             failure for something that is in fact done. The sibling bore removal reads
             BORE_NOT_FOUND the same way (well-section-cards). */
          const refusal =
            failure instanceof WorkspaceRefused ? failure : undefined;
          if (
            refusal?.status === 404 ||
            refusal?.code === 'WELL_NOT_IN_WORKSPACE'
          ) {
            removed.push(uwi);
            continue;
          }
          /* A promoted workspace refuses every removal, not this one — carrying on would just
             collect the same refusal once per well. Everything else is about this well alone,
             so the rest of the batch still gets its chance. */
          if (refusal?.code === 'WORKSPACE_PROMOTED') throw failure;
          failedRemovals.push(uwi);
        }
      }

      return {
        requestedAdds: existingUwis.length,
        skippedUwis,
        removed,
        failedRemovals,
      };
    },
    // A membership change is not safe to replay, and a refusal (a promoted workspace, a manual
    // row short of its dates) is an answer rather than a blip.
    retry: false,
    // Invalidate on SETTLED, not just success: the add is one atomic batch but the removes are
    // separate calls, so a removal that fails midway leaves earlier ones already applied.
    // Refetching on error too keeps the tree and cards from still showing a well that is in fact
    // gone — and the caller clears its staged state so a retry cannot resubmit adds that landed.
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: wellWorkspaceKeys.detail(id),
      });
      void queryClient.invalidateQueries({
        queryKey: wellWorkspaceKeys.compare(id),
      });
      // The listing carries each workspace's well count, and a well gaining or losing a
      // workspace changes its own drawer and View-menu count — the same three reads promote
      // refreshes, for the same reason.
      void queryClient.invalidateQueries({
        queryKey: wellWorkspaceKeys.list(),
      });
      void queryClient.invalidateQueries({
        queryKey: wellWorkspaceKeys.summary(),
      });
      void queryClient.invalidateQueries({
        queryKey: wellWorkspaceKeys.links(),
      });
      // The sheet's own candidate search excludes current members, so its cached pages still
      // offer a well that was just added. Only matters now that a partial Save leaves the
      // sheet open to be searched again.
      void queryClient.invalidateQueries({ queryKey: wellsKeys.all });
    },
  });

  return {
    saving: isPending,
    error: readWriteFailure(
      error,
      `Could not update the workspace’s wells. ${UNEXPLAINED_FAILURE}`
    ),
    errorCode: error instanceof WorkspaceRefused ? (error.code ?? null) : null,
    errorRow: error instanceof WorkspaceRefused ? (error.row ?? null) : null,
    save: (changes, callbacks) =>
      mutate(changes, {
        onSuccess: (outcome) => callbacks?.onSaved?.(outcome),
        onError: () => callbacks?.onError?.(addsLandedRef.current),
      }),
    reset,
  };
};

export interface WorkspacePromotion {
  /** True while the promotion is in flight. */
  busy: boolean;
  /** Why the last attempt was refused, or null. */
  error: string | null;
  promote: () => void;
  reset: () => void;
}

// Promotes a workspace, writing its staged wells back to the canonical records.
// onPromoted fires once the promotion lands, for the caller to confirm it.
export const usePromoteWorkspace = (
  id: string | number,
  onPromoted?: () => void
): WorkspacePromotion => {
  const queryClient = useQueryClient();
  const { mutate, isPending, error, reset } = useMutation<
    WellWorkspaceDetail,
    Error,
    void
  >({
    mutationFn: () => postPromotion(id),
    // A promotion is not safe to repeat, and a refusal (an unanswered gate, say) is an
    // answer rather than a blip.
    retry: false,
    onSuccess: (workspace) => {
      // Seed the detail from the response so the page shows the promoted state without
      // waiting for a round trip, then refresh the listing and summary behind it.
      queryClient.setQueryData(wellWorkspaceKeys.detail(id), workspace);
      void queryClient.invalidateQueries({
        queryKey: wellWorkspaceKeys.list(),
      });
      void queryClient.invalidateQueries({
        queryKey: wellWorkspaceKeys.summary(),
      });
      // Every well this workspace holds now reads as promoted on its own page — the
      // Workspaces drawer, the View menu count and the promoted banner all come off the
      // links read. Without this they keep saying Draft until a reload.
      void queryClient.invalidateQueries({
        queryKey: wellWorkspaceKeys.links(),
      });
      // The change-log freezes at promote, so a Compare opened beforehand is stale.
      void queryClient.invalidateQueries({
        queryKey: wellWorkspaceKeys.compare(id),
      });
      onPromoted?.();
    },
  });

  return {
    busy: isPending,
    error: readWriteFailure(
      error,
      `Could not promote the workspace. ${UNEXPLAINED_FAILURE}`
    ),
    promote: () => mutate(),
    reset,
  };
};

export interface WellStatusEditInput {
  /**
   * The staged row's id when it has one, else the well's uwi — see `patchWellStatus`. Both
   * address the same endpoint; the id is preferred because an authored well's uwi can move,
   * and the uwi is what materializes a row for a well that has none.
   */
  address: string | number;
  edit: WellStatusEdit;
}

export interface WellStatusEditor {
  /** True while a save is in flight. */
  saving: boolean;
  /** Why the last attempt was refused, or null. Carries the server's own rule messages. */
  error: string | null;
  save: (input: WellStatusEditInput, onSaved?: () => void) => void;
  reset: () => void;
}

/**
 * Saves one well's staged Well-Status edit (Epic 2, Story 2).
 *
 * The response is the whole staged well, so the detail cache is patched in place with it —
 * the card, the tree pill and the per-field pills all repaint from one response rather than
 * waiting on a refetch.
 *
 * The detail is ALSO re-read, but only while the workspace is not yet promotable: that flag
 * lives on the envelope rather than on the well, and the first edit (which materializes the
 * staged row) is what turns it on. Once it is on, a status edit cannot turn it back off, so
 * every later save would be paying for a full workspace GET to learn nothing. That mattered
 * little at one save per panel-Save; under the card's autosave it is the dominant cost of
 * the feature, and the splice above is what keeps the card repainting without it.
 * @param {string | number} id The workspace.
 * @return {WellStatusEditor} The save, its in-flight flag and its refusal.
 */
export const useEditWellStatus = (id: string | number): WellStatusEditor => {
  const queryClient = useQueryClient();
  /* Each save is stamped and only the newest answer is allowed to write. Both editors can
     have two PATCHes out at once — the card autosaves on a 600 ms debounce — and `onSuccess`
     splices the WHOLE staged row, so a late first response would reinstate the values, the
     `editedFields` pills and the `baseline` that the second had already moved past. Dropping
     the stale answer costs nothing: the server has both edits, only the changed keys are
     sent, and the next read agrees with the newer one. */
  const sentRef = useRef(0);
  const appliedRef = useRef(0);
  const { mutate, isPending, error, reset } = useMutation<
    StagedWell,
    Error,
    WellStatusEditInput
  >({
    mutationFn: ({ address, edit }) => {
      sentRef.current += 1;
      const sequence = sentRef.current;

      return patchWellStatus(id, address, edit).then((staged) => {
        appliedRef.current = sequence;
        return staged;
      });
    },
    // An edit is not safe to repeat, and a validation refusal is an answer, not a blip.
    retry: false,
    onSuccess: (staged) => {
      if (appliedRef.current < sentRef.current) return;
      queryClient.setQueryData<WellWorkspaceDetail>(
        wellWorkspaceKeys.detail(id),
        (current) =>
          current && {
            ...current,
            wells: current.wells.map((well) => {
              // Matched on the staged id first, as the identity editor does, so a row that
              // already has one is spliced by the key the save was addressed with. (The uwi
              // itself does not move: it is generated at create and is not operator-editable
              // — see OPERATOR_EDITABLE_IDENTITY_FIELDS — so the uwi arm below is the member
              // case, where the row being spliced in is the one this save just created.)
              const sameRow = staged.id !== null && well.id === staged.id;

              return sameRow || well.uwi === staged.uwi ? staged : well;
            }),
          }
      );
      // Only while Promote is still off — see the note above.
      const envelope = queryClient.getQueryData<WellWorkspaceDetail>(
        wellWorkspaceKeys.detail(id)
      );
      if (!envelope?.promotable) {
        void queryClient.invalidateQueries({
          queryKey: wellWorkspaceKeys.detail(id),
        });
      }
      // The change-log now has a row it did not have before.
      void queryClient.invalidateQueries({
        queryKey: wellWorkspaceKeys.compare(id),
      });
      /* The workspace-scoped well read carries the staged edits, and it holds them for five
         minutes (use-scoped-well). Anything drawn from it — the wellbore tearsheet and source
         modal titles, the scoped map tiles — would otherwise keep showing the pre-edit status
         until that expired. Prefix-matches the scoped key, which appends the workspace id. */
      void queryClient.invalidateQueries({
        queryKey: wellsKeys.detail(staged.uwi),
      });
    },
  });

  return {
    saving: isPending,
    error: readWriteFailure(
      error,
      `Could not save the well status. ${UNEXPLAINED_FAILURE}`
    ),
    save: (input, onSaved) => mutate(input, { onSuccess: () => onSaved?.() }),
    reset,
  };
};

export interface WellStatusVerdictInput {
  verificationItemId: number;
  /** `unanswered` clears the verdict the well already carries. */
  verdict: 'correct' | 'incorrect' | 'unanswered';
  reason?: string;
}

export interface WellStatusVerdictSetter {
  /** True while the verdict is in flight, to hold the panel's Save button. */
  saving: boolean;
  /** Why the last attempt was refused, or null. */
  error: string | null;
  set: (input: WellStatusVerdictInput, onSaved?: () => void) => void;
  reset: () => void;
}

/**
 * Marks a well's Well Status correct or incorrect (Epic 2, Story 2).
 *
 * The endpoint answers with the event, not the well, so there is nothing to seed the cache
 * with — the workspace is re-read instead, which is also what picks up any change the
 * verdict makes to the well's freeze state.
 * @param {string | number} id The workspace, for the invalidation.
 * @return {WellStatusVerdictSetter} The setter, its in-flight flag and its refusal.
 */
export const useWellStatusVerdict = (
  id: string | number
): WellStatusVerdictSetter => {
  const queryClient = useQueryClient();
  const { mutate, isPending, error, reset } = useMutation<
    void,
    Error,
    WellStatusVerdictInput
  >({
    mutationFn: ({ verificationItemId, verdict, reason }) =>
      postWellStatusVerdict(verificationItemId, verdict, reason),
    // A verdict is not safe to repeat, and a refusal (a promoted workspace, say) is an
    // answer rather than a blip.
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: wellWorkspaceKeys.detail(id),
      });
    },
  });

  return {
    saving: isPending,
    error: readWriteFailure(
      error,
      `Could not save the verdict. ${UNEXPLAINED_FAILURE}`
    ),
    set: (input, onSaved) => mutate(input, { onSuccess: () => onSaved?.() }),
    reset,
  };
};

// ── Wellbore geometry source (INS-11915) ──

/**
 * The geometries one bore can be driven from, read under the Well Workspace. Held while
 * the modal is closed so opening it is what sends the request.
 * @param {number} wellWorkspaceId The Well Workspace.
 * @param {string | null} uwi14 The bore, null while none is picked.
 * @param {boolean} enabled False holds the request.
 * @return {object} The query.
 */
export const useWellboreGeometries = (
  wellWorkspaceId: number,
  uwi14: string | null,
  enabled: boolean
) =>
  useQuery(
    queryOptions({
      queryKey: wellboreGeometryKeys.list(wellWorkspaceId, uwi14 ?? ''),
      queryFn: ({ signal }) =>
        getWellboreGeometries(wellWorkspaceId, uwi14!, signal),
      enabled: enabled && Boolean(uwi14),
      retry: READ_RETRY,
    })
  );

/** A pick as the picker writes it: a default selection, or the clearing of the standing one. */
export type GeometrySourcePick = {
  method: 'select' | 'clear';
  geometryId: number;
};

export interface SelectGeometrySource {
  /** True while the write is in flight, to hold the modal's Save. */
  saving: boolean;
  /** Why the last pick was refused, in the backend's words, or null. */
  error: string | null;
  /** Marks the geometry current for the bore, or clears the standing pick it names. */
  select: (pick: GeometrySourcePick) => void;
  /** Clears the last refusal, so every open starts clean. */
  reset: () => void;
}

/**
 * Mark one geometry current for the bore (the Source modal's Save). A 201 refreshes the
 * geometries read and the host's rows; a 422's reason travels out as the error.
 * @param {number} wellWorkspaceId The Well Workspace.
 * @param {string | null} uwi14 The bore.
 * @param {Function} onSaved Called once the pick has landed.
 * @return {SelectGeometrySource} The write, its state and the last refusal.
 */
export const useSelectGeometrySource = (
  wellWorkspaceId: number,
  uwi14: string | null,
  onSaved: () => void
): SelectGeometrySource => {
  const queryClient = useQueryClient();
  const { mutate, isPending, error, reset } = useMutation<
    unknown,
    Error,
    GeometrySourcePick
  >({
    mutationFn: ({ method, geometryId }) => {
      const write =
        method === 'clear'
          ? deleteGeometrySourceSelection
          : postGeometrySourceSelection;
      return write(wellWorkspaceId, uwi14!, geometryId);
    },
    // A refusal is an answer about the pick, not a blip to retry through.
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: wellboreGeometryKeys.all,
      });
      onSaved();
    },
  });

  return {
    saving: isPending,
    error: readWriteFailure(
      error,
      `Could not change the wellbore geometry source. ${UNEXPLAINED_FAILURE}`
    ),
    select: (pick) => {
      if (uwi14) mutate(pick);
    },
    reset,
  };
};

// ── Unit assignment for an authored well (Epic 3 Story 1) ──

/**
 * The unit candidates for a well authored in this workspace.
 *
 * Held until the well has a staged-well id: a member well with no staged row yet is
 * addressed by uwi and has no canonical-less candidates to list. Empty until the well has a
 * wellbore geometry, because the intersection has nothing to run against — that is the
 * backend's answer, not a guard here, so the card reports "no units intersect" either way.
 * @param {number} workspaceId The workspace.
 * @param {number | null} stagedWellId The authored well, null while unresolved.
 * @return {object} The query.
 */
export const useWellUnitCandidates = (
  workspaceId: number,
  stagedWellId: number | null
) =>
  useQuery(
    queryOptions({
      queryKey: wellUnitCandidateKeys.list(workspaceId, stagedWellId ?? 0),
      queryFn: ({ signal }) =>
        getWellUnitCandidates(workspaceId, stagedWellId!, signal),
      enabled: Number.isFinite(stagedWellId),
      retry: READ_RETRY,
    })
  );

export interface AssignUnit {
  /** True while the pick is in flight, to hold the Assign button. */
  saving: boolean;
  /** Why the last pick was refused, in the backend's words, or null. */
  error: string | null;
  /** The refusal's own code, for a caller that must route on WHICH refusal it was. */
  errorCode: string | null;
  /** Stages the explicit pick. The unit is named, not numbered — see postUnitAssignment. */
  assign: (unitName: string) => void;
  reset: () => void;
}

/**
 * Stage the operator's explicit unit pick (the card's "Assign to well").
 *
 * The response IS the refreshed candidate list, so it seeds the cache directly rather than
 * invalidating and paying for a second round trip — the list is what the card repaints from,
 * and the server has just computed it.
 *
 * The workspace detail is invalidated too: staging an assignment is staged work, so a
 * membership-only workspace becomes promotable on the first pick and the header's Promote
 * would otherwise stay as it was until a reload.
 *
 * ON A REFUSAL THE LIST IS RE-READ. A unit the backend says is not a candidate is evidence
 * the list this pick came from is out of date — most often because removing or rebuilding
 * the primary bore moved the well and the backend cleared its pick. Seeding on success but
 * doing nothing on failure would leave the card showing the very rows that produced the
 * refusal, with the rejected one still selected and one click away from being sent again.
 * @param {number} workspaceId The workspace.
 * @param {number | null} stagedWellId The authored well.
 * @return {AssignUnit} The write, its state and the last refusal.
 */
export const useAssignUnit = (
  workspaceId: number,
  stagedWellId: number | null
): AssignUnit => {
  const queryClient = useQueryClient();
  const { mutate, isPending, error, reset } = useMutation<
    WellIntersectionAnalysisResponse,
    Error,
    string
  >({
    mutationFn: (unitName) =>
      postUnitAssignment(workspaceId, stagedWellId!, unitName),
    // A refusal is an answer about the pick, not a blip to retry through.
    retry: false,
    onSuccess: (candidates) => {
      queryClient.setQueryData(
        wellUnitCandidateKeys.list(workspaceId, stagedWellId ?? 0),
        candidates
      );
      void queryClient.invalidateQueries({
        queryKey: wellWorkspaceKeys.detail(workspaceId),
      });
    },
    onError: () => {
      void queryClient.invalidateQueries({
        queryKey: wellUnitCandidateKeys.list(workspaceId, stagedWellId ?? 0),
      });
    },
  });

  return {
    saving: isPending,
    error: readWriteFailure(
      error,
      `Could not assign the unit. ${UNEXPLAINED_FAILURE}`
    ),
    errorCode: error instanceof WorkspaceRefused ? (error.code ?? null) : null,
    assign: (unitName) => {
      if (Number.isFinite(stagedWellId)) mutate(unitName);
    },
    reset,
  };
};

export interface WellIdentityEditInput {
  /** The staged row being edited — a created well has no uwi worth addressing it by. */
  stagedWellId: number;
  edit: WellIdentityEdit;
}

export interface WellIdentityEditor {
  /** True while a save is in flight, to hold the field. */
  saving: boolean;
  /** Why the last save was refused, in the backend's words, or null. */
  error: string | null;
  save: (input: WellIdentityEditInput, onSaved?: () => void) => void;
  reset: () => void;
}

/**
 * Save a well's identity edit — Lease Name, Well Number, Current Operator (Epic 3 Story 1).
 *
 * UWI is NOT here: the backend generates it and a user never types one, so the card shows it
 * read-only. The displayed Well Name is likewise derived from the two fields below rather
 * than written, which is why this saves them separately — a joined name cannot be split back.
 *
 * Seeds the detail cache from the RESPONSE rather than from what was typed, so the card
 * repaints with the server's derived name (and its `editedFields` pills) rather than with a
 * locally guessed one. Matched on the staged id where there is one, falling back to uwi for a
 * member well whose first edit has only just materialized a row.
 * @param {string | number} id The workspace.
 * @return {WellIdentityEditor} The save, its in-flight flag and its refusal.
 */
export const useEditWellIdentity = (
  id: string | number
): WellIdentityEditor => {
  const queryClient = useQueryClient();
  /* Each save is stamped and only the newest answer is allowed to write. Both editors can
     have two PATCHes out at once — the card autosaves on a 600 ms debounce — and `onSuccess`
     splices the WHOLE staged row, so a late first response would reinstate the values, the
     `editedFields` pills and the `baseline` that the second had already moved past. Dropping
     the stale answer costs nothing: the server has both edits, only the changed keys are
     sent, and the next read agrees with the newer one. */
  const sentRef = useRef(0);
  const appliedRef = useRef(0);
  const { mutate, isPending, error, reset } = useMutation<
    StagedWell,
    Error,
    WellIdentityEditInput
  >({
    mutationFn: ({ stagedWellId, edit }) => {
      sentRef.current += 1;
      const sequence = sentRef.current;

      return patchWellIdentity(id, stagedWellId, edit).then((staged) => {
        appliedRef.current = sequence;
        return staged;
      });
    },
    // An edit is not safe to repeat, and a validation refusal is an answer, not a blip.
    retry: false,
    onSuccess: (staged) => {
      if (appliedRef.current < sentRef.current) return;
      queryClient.setQueryData<WellWorkspaceDetail>(
        wellWorkspaceKeys.detail(id),
        (current) =>
          current && {
            ...current,
            wells: current.wells.map((well) => {
              const sameRow = staged.id !== null && well.id === staged.id;
              return sameRow || well.uwi === staged.uwi ? staged : well;
            }),
          }
      );
      // `promotable` and `promotionBlockers` ride the envelope, not the well — naming a well
      // is half the new-well promote gate, so the header has to be re-read for Promote to
      // enable and for its tooltip to stop naming the name as missing.
      void queryClient.invalidateQueries({
        queryKey: wellWorkspaceKeys.detail(id),
      });
      // The change-log now has a row it did not have before.
      void queryClient.invalidateQueries({
        queryKey: wellWorkspaceKeys.compare(id),
      });
      // Same as the status edit: the scoped read holds the name for five minutes otherwise.
      void queryClient.invalidateQueries({
        queryKey: wellsKeys.detail(staged.uwi),
      });
    },
  });

  return {
    saving: isPending,
    error: readWriteFailure(
      error,
      `Could not save the well. ${UNEXPLAINED_FAILURE}`
    ),
    save: (input, onSaved) => mutate(input, { onSuccess: () => onSaved?.() }),
    reset,
  };
};

/**
 * The LTD sticks the Add Wellbore sheet lists.
 *
 * Requested only while the sheet is open, so closing it does not keep a read alive. Serves
 * the previous page while the next loads, so narrowing a facet does not blank the table.
 * @param {LtdCandidatesQuery} query The rail state: facets, text, extent and paging.
 * @param {boolean} enabled False holds the request.
 * @return {object} The query.
 */
export const useLtdCandidates = (query: LtdCandidatesQuery, enabled: boolean) =>
  useQuery(
    queryOptions({
      queryKey: ltdCandidateKeys.list(query),
      queryFn: ({ signal }) => getLtdCandidates(query, signal),
      enabled,
      retry: READ_RETRY,
      // v5 spelling: `keepPreviousData: true` is a v4 option this version silently ignores,
      // which would flash the facets and the table empty on every filter change.
      placeholderData: keepPreviousData,
    })
  );

/**
 * Which bore a build writes to.
 *
 * A discriminated union rather than an optional uwi14: `{ mode: 'rebuild' }` without a bore
 * would compile and PATCH `…/wellbores/undefined`, which — because the provider runs before
 * the bore is resolved — comes back as a 422 about the SELECTION rather than a 404 about the
 * bore, and would read as the reader's mistake.
 */
export type WellboreTarget =
  { mode: 'create' } | { mode: 'rebuild'; completionUwi14: string };

export interface BuildWellbore {
  /** True while the build is in flight, to hold the sheet's action. */
  saving: boolean;
  /** Why the last attempt was refused, in the backend's words, or null. */
  error: string | null;
  /** The refusal's own code, for a caller that must route on WHICH refusal it was. */
  errorCode: string | null;
  /** The slots a stale-stick refusal named — exactly the ones to drop from the selection. */
  staleSlotIds: number[] | null;
  build: (
    payload: WellborePayload,
    onBuilt?: (result: WellboreBuilt) => void
  ) => void;
  reset: () => void;
}

/**
 * Build a wellbore for an authored well from the selected LTD sticks (Epic 3 Story 1).
 *
 * ONE hook for both writes. `create` adds a new bore — always, since the backend dropped the
 * upsert that used to let a POST overwrite the primary — and `rebuild` replaces one bore's
 * geometry in place. They differ only in the request; everything below is identical, and the
 * invalidation list in particular carries reasoning that would drift if it were written out
 * twice.
 *
 * The backend owns the derivation of record and the geographic recalc; the sheet's chain is
 * only a preview. On success everything downstream of the geometry is re-read:
 *
 *  - the workspace detail, because a geometry is half the new-well promote gate;
 *  - the canonical well, where the recomputed attributes and the HATEOAS hrefs live — they
 *    are no longer echoed on the response, so this read is the only way the cards repaint;
 *  - the unit candidates, because the backend clears a unit pick made against the OLD
 *    geometry and the intersection is recomputed against the new one;
 *  - the change-log, which now has rows it did not have before.
 *
 * All four fire unconditionally, including for a rebuild of a SECONDARY bore, where the
 * backend clears neither the pick nor the well's fields. Skipping them there would save one
 * refetch of unchanged data in exchange for branching on which bore is primary — a fact
 * nothing serves to the client, so it could only be guessed. A spare read is cheaper than a
 * wrong cache.
 * @param {string | number} workspaceId The workspace.
 * @param {number | null} stagedWellId The authored well.
 * @param {string} uwi The well's uwi, for the canonical read's key.
 * @param {WellboreTarget} target Whether this adds a bore or rebuilds one.
 * @return {BuildWellbore} The write, its state and the last refusal.
 */
export const useBuildWellbore = (
  workspaceId: string | number,
  stagedWellId: number | null,
  uwi: string,
  target: WellboreTarget
): BuildWellbore => {
  const queryClient = useQueryClient();
  const { mutate, isPending, error, reset } = useMutation<
    WellboreBuilt,
    Error,
    WellborePayload
  >({
    mutationFn: (payload) => {
      if (target.mode === 'rebuild') {
        return patchWellbore(
          workspaceId,
          stagedWellId!,
          target.completionUwi14,
          payload
        );
      }

      return postWellbore(workspaceId, stagedWellId!, payload);
    },
    // A refusal is an answer about the selection, not a blip to retry through.
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: wellWorkspaceKeys.detail(workspaceId),
      });
      void queryClient.invalidateQueries({ queryKey: wellsKeys.detail(uwi) });
      void queryClient.invalidateQueries({
        queryKey: wellUnitCandidateKeys.all,
      });
      void queryClient.invalidateQueries({
        queryKey: wellWorkspaceKeys.compare(workspaceId),
      });
    },
  });

  return {
    saving: isPending,
    error: readWriteFailure(
      error,
      target.mode === 'rebuild'
        ? `Could not rebuild the wellbore. ${UNEXPLAINED_FAILURE}`
        : `Could not add the wellbore. ${UNEXPLAINED_FAILURE}`
    ),
    errorCode: error instanceof WorkspaceRefused ? (error.code ?? null) : null,
    staleSlotIds:
      error instanceof WorkspaceRefused ? (error.staleSlotIds ?? null) : null,
    build: (payload, onBuilt) => {
      if (Number.isFinite(stagedWellId)) {
        mutate(payload, { onSuccess: (result) => onBuilt?.(result) });
      }
    },
    reset,
  };
};

export interface RemoveWellbore {
  /** True while the removal is in flight, to hold the confirm's action. */
  removing: boolean;
  /** Why the last attempt was refused, in the backend's words, or null. */
  error: string | null;
  /** The refusal's own code, for a caller that must route on WHICH refusal it was. */
  errorCode: string | null;
  remove: (completionUwi14: string, onRemoved?: () => void) => void;
  reset: () => void;
}

/**
 * Remove one drawn bore from an authored well.
 *
 * THE SAME FOUR INVALIDATIONS AS `useBuildWellbore`, for the same reasons — see its comment,
 * which transfers line for line. Removing the last bore flips the promote gate, removing the
 * primary repoints it and recomputes the well's fields, and either can add change-log rows.
 * As there, they fire unconditionally: the response carries only the bore structure, so the
 * client cannot tell a primary removal from a secondary one without guessing at a fact
 * nothing serves it, and a spare read is cheaper than a wrong cache.
 *
 * WHAT THIS DOES NOT REFRESH is the wellbores table itself, which is not React Query at all
 * — it reads through a jotai atom and repaints only on its host's remount. The card owns
 * that, via the same `refreshWorkspace` the add and rebuild paths already call.
 * @param {string | number} workspaceId The workspace.
 * @param {number | null} stagedWellId The authored well.
 * @param {string} uwi The well's uwi, for the canonical read's key.
 * @return {RemoveWellbore} The write, its state and the last refusal.
 */
export const useRemoveWellbore = (
  workspaceId: string | number,
  stagedWellId: number | null,
  uwi: string
): RemoveWellbore => {
  const queryClient = useQueryClient();
  const { mutate, isPending, error, reset } = useMutation<
    BoreChange,
    Error,
    string
  >({
    mutationFn: (completionUwi14) =>
      deleteWellbore(workspaceId, stagedWellId!, completionUwi14),
    // A refusal is an answer about the bore, not a blip to retry through — and a retried
    // DELETE of a bore the first call already removed would answer BORE_NOT_FOUND.
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: wellWorkspaceKeys.detail(workspaceId),
      });
      void queryClient.invalidateQueries({ queryKey: wellsKeys.detail(uwi) });
      void queryClient.invalidateQueries({
        queryKey: wellUnitCandidateKeys.all,
      });
      void queryClient.invalidateQueries({
        queryKey: wellWorkspaceKeys.compare(workspaceId),
      });
    },
  });

  return {
    removing: isPending,
    error: readWriteFailure(
      error,
      `Could not remove the wellbore. ${UNEXPLAINED_FAILURE}`
    ),
    errorCode: error instanceof WorkspaceRefused ? (error.code ?? null) : null,
    remove: (completionUwi14, onRemoved) => {
      if (Number.isFinite(stagedWellId)) {
        mutate(completionUwi14, { onSuccess: () => onRemoved?.() });
      }
    },
    reset,
  };
};

export interface SetPrimaryWellbore {
  /** True while the switch is in flight, to hold the radio column. */
  setting: boolean;
  /** Which bore is being switched to, so only that row shows the pending state. */
  pendingUwi14: string | null;
  /** Why the last attempt was refused, in the backend's words, or null. */
  error: string | null;
  setPrimary: (completionUwi14: string) => void;
  reset: () => void;
}

/**
 * Make a drawn bore the well's primary (Epic 3 Story 1).
 *
 * THE SAME FOUR INVALIDATIONS as the build and remove paths — see `useBuildWellbore`. The
 * pointer move is cheap but its consequences are not: the backend clears the well's staged
 * unit pick and recomputes its geographic fields from the new primary's surface hole, and
 * none of that is echoed back.
 *
 * `pendingUwi14` is tracked here rather than left to the caller because the radio column
 * needs to know WHICH row is mid-flight, not merely that something is. Without it every
 * radio would have to disable together, which reads as the whole control breaking.
 * @param {string | number} workspaceId The workspace.
 * @param {number | null} stagedWellId The authored well.
 * @param {string} uwi The well's uwi, for the canonical read's key.
 * @return {SetPrimaryWellbore} The write, its state and the last refusal.
 */
export const useSetPrimaryWellbore = (
  workspaceId: string | number,
  stagedWellId: number | null,
  uwi: string
): SetPrimaryWellbore => {
  const queryClient = useQueryClient();
  const [pendingUwi14, setPendingUwi14] = useState<string | null>(null);
  const { mutate, isPending, error, reset } = useMutation<
    BoreChange,
    Error,
    string
  >({
    mutationFn: (completionUwi14) =>
      patchPrimaryWellbore(workspaceId, stagedWellId!, completionUwi14),
    // A refusal is an answer about the bore, not a blip to retry through.
    retry: false,
    onSettled: () => setPendingUwi14(null),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: wellWorkspaceKeys.detail(workspaceId),
      });
      void queryClient.invalidateQueries({ queryKey: wellsKeys.detail(uwi) });
      void queryClient.invalidateQueries({
        queryKey: wellUnitCandidateKeys.all,
      });
      void queryClient.invalidateQueries({
        queryKey: wellWorkspaceKeys.compare(workspaceId),
      });
    },
  });

  return {
    setting: isPending,
    pendingUwi14,
    error: readWriteFailure(
      error,
      `Could not change the primary wellbore. ${UNEXPLAINED_FAILURE}`
    ),
    setPrimary: (completionUwi14) => {
      if (!Number.isFinite(stagedWellId)) return;
      setPendingUwi14(completionUwi14);
      mutate(completionUwi14);
    },
    reset,
  };
};

// ── Current Operator typeahead (moved from use-operator-options.ts) ──

/** Wait after the last keystroke before asking the index again. */
const OPERATOR_DEBOUNCE_MS = 300;

export interface OperatorOptions {
  /** What the combo lists. Empty while the first read is out. */
  items: string[];
  /** Feed the reader's typing in; the request trails it by a debounce. */
  search: (text: string) => void;
}

/**
 * Operators to offer on the Current Operator field.
 *
 * A typeahead rather than a fixed list: `current_operator` is free text on the well, so
 * the options are whatever the catalog already holds — read from the shared
 * `POST /wells/field_lookup` aggregation, the same one the AFE catalog's operator field
 * uses. Nothing is cached across texts (`cacheTime: 0` would be wrong here — the catalog
 * barely moves), but each text is its own key, so going back to a prefix is instant.
 *
 * The list is a SUGGESTION, never a constraint. The field keeps free entry, because a well
 * built from a document can name an operator the catalog has never seen; that is also why
 * this never fails loudly — an index that is down leaves the reader typing, which is the
 * behaviour they would have had anyway.
 * @param {boolean} enabled False while the field cannot be edited, so a read-only card
 *   makes no requests.
 * @return {OperatorOptions} The options and the search input.
 */
export const useOperatorOptions = (enabled: boolean): OperatorOptions => {
  const [text, setText] = useState('');
  const [settled, setSettled] = useState('');

  // React Query has no debounce of its own, and the key is the text, so the key trails
  // the typing rather than firing a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setSettled(text), OPERATOR_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [text]);

  const { data } = useQuery(
    queryOptions<string[], Error>({
      queryKey: ['well-operators', settled],
      queryFn: () => fetchOperatorOptions(settled),
      enabled,
      // A suggestion list is not worth a retry storm, and a stale one is still useful.
      retry: false,
      // v5 spelling: `keepPreviousData: true` is a v4 option this version ignores, which would
      // blank the suggestion list between keystrokes instead of holding the last one.
      placeholderData: keepPreviousData,
      staleTime: 5 * 60 * 1000,
    })
  );

  return { items: data ?? [], search: setText };
};

// ── Well Status autosave (was use-status-autosave.ts) ──

/**
 * How long a commit waits before it is sent.
 *
 * NOT a keystroke debounce — a commit is already a discrete act. It buys three things:
 *
 *  1. The held case has to send more than one key. Setting the status to `producing` on a
 *     well with no spud date is held until the spud date arrives, and the two have to
 *     travel together or the first would be refused on its own.
 *  2. Adjacent commits are the common case, not the edge: picking a status closes its
 *     editor, and the same click often lands on the next row's pencil.
 *  3. It stops the held warning flashing up for a sequence the reader finishes quickly.
 *
 * Between this app's 400ms (the Add Wellbore preview) and 800ms (`CHART_AUTOSAVE_MS`):
 * 800 reads as lag after a deliberate commit, 400 is too short for (2).
 */
export const STATUS_AUTOSAVE_MS = 600;

/**
 * The rows a reader can change. Exactly the keys `FIELD_TO_SERVER_KEY` maps, so a draft
 * can never hold a field the endpoint has no column for — Last Production Date is derived
 * everywhere and is absent by construction rather than by a guard.
 */
type EditableField = 'status' | EditableDateField | 'firstProductionDate';

const EDITABLE_ROWS: EditableField[] = [
  'status',
  'permitDate',
  'spudDate',
  'rigReleaseDate',
  'completionDate',
  'firstProductionDate',
];

/**
 * @param {WellStatusRow} row The row being edited.
 * @return {boolean} Whether it is a field this hook autosaves.
 */
const isEditableField = (row: WellStatusRow): row is EditableField =>
  (EDITABLE_ROWS as WellStatusRow[]).includes(row);

/** `Pick`, not `Record<..., string | null>`: it keeps `status` a token rather than any string. */
type Draft = Partial<Pick<StatusEditValues, EditableField>>;

/**
 * A stable identity for the effective values, so the validation memo can be checked
 * statically — the object itself is rebuilt every render and would defeat it.
 * @param {StatusEditValues} values The effective values.
 * @return {string} A signature that changes only when a value does.
 */
const signature = (values: StatusEditValues): string => JSON.stringify(values);

export interface StatusAutosave {
  /** What the card renders: the server's row with any unsaved commits laid over it. */
  values: StatusEditValues;
  /** Rows committed but not yet confirmed by a response. */
  pending: ReadonlySet<WellStatusRow>;
  /** Broken rules over `values`. Non-empty is what holds the save. */
  errors: StatusEditError[];
  /** True while a request is out. */
  saving: boolean;
  /** A refusal the client-side mirror did not predict. */
  error: string | null;
  /** Whether this well's server rules accept an operator first production date. */
  firstProductionEditable: boolean;
  /** Whether a row can be edited at all. */
  editable: (row: WellStatusRow) => boolean;
  commit: (row: WellStatusRow, value: string | null) => void;
  /** Throws the unsaved draft away — the only undo, since a dropdown commits on pick. */
  revert: () => void;
  dismissError: () => void;
}

/**
 * The server's own values for the well, as the validator wants them.
 * @param {StagedWell} well The scoped staged well.
 * @return {StatusEditValues} Its status and lifecycle dates.
 */
const fromWell = (well: StagedWell): StatusEditValues => ({
  status: well.status,
  permitDate: well.permitDate,
  spudDate: well.spudDate,
  rigReleaseDate: well.rigReleaseDate,
  completionDate: well.completionDate,
  firstProductionDate: well.firstProductionDate,
  lastProductionDate: well.lastProductionDate,
});

const EMPTY_VALUES: StatusEditValues = {
  status: null,
  permitDate: null,
  spudDate: null,
  rigReleaseDate: null,
  completionDate: null,
  firstProductionDate: null,
  lastProductionDate: null,
};

/**
 * Autosave for the Well Status card: a draft, a validation gate, and a debounced save.
 *
 * THE DRAFT IS A DELTA, not a copy of the well. That is what lets an unsaved edit survive
 * a background refetch: `values` recomputes from whatever the server last said plus the
 * rows the reader actually touched, so nothing has to be re-seeded and nothing an
 * unrelated response carries can wipe what is on screen. (The Well Details card's
 * `useEffect(() => setValue(committed), [committed])` idiom does the opposite, and would.)
 *
 * NOTHING IS SENT WHILE THE DRAFT IS INVALID. `validateStatusEdit` mirrors the server's
 * `:status_edit` rules, so a status whose required dates are missing holds the save and
 * names the gap instead of making a request that would be refused. The commit that
 * supplies the missing date re-validates clean and arms the timer in the same handler, so
 * "save once it becomes valid" needs no watcher.
 *
 * THE PAYLOAD IS THE DELTA TOO — only the keys the reader changed. There is no optimistic
 * locking on this endpoint, so re-asserting four untouched values would write them back
 * over anything that moved underneath (ingest, another operator) since the card last read.
 * The server validates the whole resulting row whatever the payload's width, and
 * recomputes the "Edited" pills from baseline on every save, so nothing is lost by
 * narrowing it.
 * @param {string | number} workspaceId The workspace.
 * @param {StagedWell} well The scoped well, absent while the page resolves.
 * @param {boolean} enabled False on a promoted workspace, which refuses every edit.
 * @return {StatusAutosave} The effective values, the gate, and the commit.
 */
export const useStatusAutosave = (
  workspaceId: string | number,
  well: StagedWell | undefined,
  enabled: boolean
): StatusAutosave => {
  const editor = useEditWellStatus(workspaceId);
  /* Reached through a ref, never a dep: useMutation rebuilds `editor` every render, so
     listing it would rebuild the debouncer each time and silently drop whatever save was
     pending. The edit panel worked around the same hazard with an eslint-disable. */
  const editorRef = useRef(editor);
  // eslint-disable-next-line react-hooks/refs -- latest-value ref: the debouncer is built once with [] deps, so a pending save must read the CURRENT editor rather than the one captured when it was built.
  editorRef.current = editor;

  /* Where a save is SENT. A member well has no staged row until its first edit creates one,
     so it is addressed by uwi until then and by id afterwards — this deliberately moves. */
  const address = well ? (well.id ?? well.uwi) : null;
  /* Which well the draft BELONGS to, which is a different question. The uwi is the only
     stable answer: a staged well's uwi is generated at create and is not operator-editable
     (OPERATOR_EDITABLE_IDENTITY_FIELDS is lease_name / well_number / current_operator), while
     `address` flips the moment the first save materializes the row. Keying the reset below on
     `address` read that flip as "the reader stepped to another well" and wiped a draft that
     was still being edited. */
  const identity = well?.uwi ?? null;
  const [draft, setDraft] = useState<Draft>({});
  /* Dropped during render rather than in the flush effect below: child effects run first,
     so the next well's rows would see this draft for one render and open on its errors. */
  const [draftIdentity, setDraftIdentity] = useState(identity);
  if (draftIdentity !== identity) {
    setDraftIdentity(identity);
    setDraft({});
  }
  /** The keys of the request currently out, so a 200 prunes exactly those. */
  const inFlightRef = useRef<EditableField[]>([]);

  const served = well ? fromWell(well) : EMPTY_VALUES;
  const values: StatusEditValues = { ...served, ...draft };

  const firstProductionEditable = well
    ? canEditFirstProductionDate(well)
    : false;
  const baselineFirstProductionDate =
    well?.baseline?.firstProductionDate ?? null;

  const valuesSignature = signature(values);
  const errors = useMemo(
    () =>
      validateStatusEdit(values, {
        firstProductionEditable,
        baselineFirstProductionDate,
      }),
    // Keyed on the signature, not on `values` — the object is a new identity every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the debounced saver is created once; re-creating it on every dep change would drop a pending save.
    [valuesSignature, firstProductionEditable, baselineFirstProductionDate]
  );

  const debouncedSave = useMemo(
    () =>
      debounce(
        // eslint-disable-next-line react-hooks/refs -- the ref is read when the DEBOUNCE FIRES, which is always after render, never during it.
        (
          target: string | number,
          keys: EditableField[],
          edit: Record<string, string | null>
        ) => {
          inFlightRef.current = keys;
          editorRef.current.save({ address: target, edit }, () => {
            // On a 200 the response IS the row, so the keys that were sent have nothing
            // left to hold. Pruning by the keys SENT rather than by equality also covers
            // the server ignoring one (a first production date it slices back out), which
            // equality would leave in the delta forever and re-send on every later commit.
            const sent = new Set<string>(inFlightRef.current);
            inFlightRef.current = [];
            setDraft((current) => {
              const next: Draft = {};
              (Object.keys(current) as EditableField[]).forEach((key) => {
                if (!sent.has(key)) {
                  // Narrowed per key so `status` keeps its token type through the copy.
                  Object.assign(next, { [key]: current[key] });
                }
              });

              return next;
            });
          });
        },
        STATUS_AUTOSAVE_MS
      ),
    []
  );

  // The payload carries its own address, so a flush after the stepper moved still writes
  // to the well the edit belonged to.
  useEffect(() => () => debouncedSave.flush(), [debouncedSave]);

  /* The card does NOT unmount when the reader steps to another well — every card stays
     mounted and only the scoped well changes — so the unmount flush above never fires on
     a stepper move and this has to do it. A held-INVALID draft cannot be flushed (there is
     nothing legal to send) and is dropped; it was on screen with a visible warning. The
     pending save carries its own payload, so the draft is already gone by now. */
  const previousIdentity = useRef<string | null>(null);
  useEffect(() => {
    const previous = previousIdentity.current;
    if (previous !== null && previous !== identity) debouncedSave.flush();
    previousIdentity.current = identity;
  }, [identity, debouncedSave]);

  const editable = useCallback(
    (row: WellStatusRow) => {
      if (!enabled || !well) return false;
      if (row === 'firstProductionDate') return firstProductionEditable;

      return isEditableField(row);
    },
    [enabled, well, firstProductionEditable]
  );

  const commit = useCallback(
    (row: WellStatusRow, value: string | null) => {
      if (!enabled || !well || address === null) return;
      if (!isEditableField(row)) return;
      if (row === 'firstProductionDate' && !firstProductionEditable) return;

      const key = row;
      const nextDraft: Draft = { ...draft };
      if (value === fromWell(well)[key]) {
        // Back to what the server holds: not an edit. Dropping the key rather than
        // recording a no-op keeps a mistype-then-correct from sending anything.
        delete nextDraft[key];
      } else {
        // The status editor only ever yields a token; the dates are plain ISO strings.
        Object.assign(nextDraft, { [key]: value as WellStatusToken | null });
      }
      setDraft(nextDraft);

      const keys = Object.keys(nextDraft) as EditableField[];
      if (keys.length === 0) {
        // Never send `{}` — the endpoint's `params.require(:well)` would 400.
        debouncedSave.cancel();

        return;
      }

      const nextValues = { ...fromWell(well), ...nextDraft };
      const nextErrors = validateStatusEdit(nextValues, {
        firstProductionEditable,
        baselineFirstProductionDate,
      });

      /* Decided synchronously rather than from an effect on `errors`: this card re-renders
         constantly (the stepper, a detail refetch, the dock measuring itself), and an
         effect would re-arm the timer on every one of them. */
      if (nextErrors.length > 0) {
        debouncedSave.cancel();

        return;
      }

      debouncedSave(
        address,
        keys,
        Object.fromEntries(
          keys.map((field) => [
            FIELD_TO_SERVER_KEY[field],
            nextDraft[field] ?? null,
          ])
        )
      );
    },
    [
      enabled,
      well,
      address,
      draft,
      firstProductionEditable,
      baselineFirstProductionDate,
      debouncedSave,
    ]
  );

  const revert = useCallback(() => {
    debouncedSave.cancel();
    setDraft({});
  }, [debouncedSave]);

  return {
    values,
    pending: new Set(Object.keys(draft) as WellStatusRow[]),
    errors,
    saving: editor.saving,
    error: editor.error,
    firstProductionEditable,
    editable,
    commit,
    revert,
    dismissError: editor.reset,
  };
};

// ── Well Details identity autosave (was use-identity-autosave.ts) ──

/** The three operator-writable identity fields, and the keys the endpoint takes. */
export const IDENTITY_FIELDS = [
  'leaseName',
  'wellNumber',
  'currentOperator',
] as const;

export type IdentityField = (typeof IDENTITY_FIELDS)[number];

export const IDENTITY_SERVER_KEY: Record<IdentityField, string> = {
  leaseName: 'lease_name',
  wellNumber: 'well_number',
  currentOperator: 'current_operator',
};

export const IDENTITY_LABELS: Record<IdentityField, string> = {
  leaseName: 'Lease Name',
  wellNumber: 'Well Number',
  currentOperator: 'Current Operator',
};

type IdentityDraft = Partial<Record<IdentityField, string | null>>;

export interface IdentityAutosave {
  /** What the card renders: the server's values with any unsaved commits laid over them. */
  values: Record<IdentityField, string | null>;
  /** Rows committed but not yet confirmed by a response. */
  pending: ReadonlySet<InlineRow>;
  saving: boolean;
  error: string | null;
  editable: (row: InlineRow) => boolean;
  commit: (row: InlineRow, value: string | null) => void;
  dismissError: () => void;
}

/**
 * @param {InlineRow} row The row being edited.
 * @return {boolean} Whether it is one of the identity fields this hook autosaves.
 */
const isIdentityField = (row: InlineRow): row is IdentityField =>
  (IDENTITY_FIELDS as readonly InlineRow[]).includes(row);

/**
 * Autosave for the Well Details card's identity fields.
 *
 * The same engine as the Well Status card's — a DELTA draft laid over the server's values,
 * a debounced save that carries its own address, and a flush on unmount and on the stepper
 * moving — so the two cards save the same way and a reader cannot tell them apart. It is
 * deliberately NOT the status hook with a flag: these fields have a different mutation, a
 * different address and no cross-field rules, and folding them together would mean a
 * conditional at every one of those points.
 *
 * WHAT IS MISSING HERE IS THE GATE. Well Status holds a save while the draft breaks a
 * required-dates rule; identity has no such rule — any combination of the three is a legal
 * row, including all three blank. A blank name only blocks PROMOTE, which is a gate on the
 * workspace rather than a validation on the edit, and is reported by the header.
 *
 * ADDRESSED BY THE STAGED ROW'S ID ONLY. Unlike the status edit, which can materialize a
 * row by uwi, `patchWellIdentity` takes an id — so a membership-only well with no staged
 * row yet is simply not editable here, and the card renders its identity read-only.
 * @param {string | number} workspaceId The workspace.
 * @param {StagedWell} well The scoped well, absent while the page resolves.
 * @param {boolean} enabled False on a promoted workspace, which refuses every edit.
 * @return {IdentityAutosave} The effective values and the commit.
 */
export const useIdentityAutosave = (
  workspaceId: string | number,
  well: StagedWell | undefined,
  enabled: boolean
): IdentityAutosave => {
  const editor = useEditWellIdentity(workspaceId);
  // Through a ref, never a dep — see the status hook: useMutation rebuilds this every
  // render, and listing it would rebuild the debouncer and drop the pending save.
  const editorRef = useRef(editor);
  // eslint-disable-next-line react-hooks/refs -- latest-value ref: as in the identity autosave above, the debouncer is built once and must save against the current editor.
  editorRef.current = editor;

  const address = well?.id ?? null;
  const [draft, setDraft] = useState<IdentityDraft>({});
  const inFlightRef = useRef<IdentityField[]>([]);

  const served: Record<IdentityField, string | null> = {
    leaseName: well?.leaseName ?? null,
    wellNumber: well?.wellNumber ?? null,
    currentOperator: well?.currentOperator ?? null,
  };
  const values = { ...served, ...draft };

  const debouncedSave = useMemo(
    () =>
      debounce(
        // eslint-disable-next-line react-hooks/refs -- the ref is read when the DEBOUNCE FIRES, which is always after render, never during it.
        (
          target: number,
          keys: IdentityField[],
          edit: Record<string, string | null>
        ) => {
          inFlightRef.current = keys;
          editorRef.current.save({ stagedWellId: target, edit }, () => {
            const sent = new Set<string>(inFlightRef.current);
            inFlightRef.current = [];
            setDraft((current) => {
              const next: IdentityDraft = {};
              (Object.keys(current) as IdentityField[]).forEach((key) => {
                if (!sent.has(key)) next[key] = current[key];
              });

              return next;
            });
          });
        },
        STATUS_AUTOSAVE_MS
      ),
    []
  );

  useEffect(() => () => debouncedSave.flush(), [debouncedSave]);

  // The card stays mounted when the stepper moves, so the unmount flush never fires on a
  // well change and this has to do it. The payload carries its own address.
  const previousAddress = useRef<number | null>(null);
  useEffect(() => {
    const previous = previousAddress.current;
    if (previous !== null && previous !== address) {
      debouncedSave.flush();
      setDraft({});
    }
    previousAddress.current = address;
  }, [address, debouncedSave]);

  const editable = useCallback(
    (row: InlineRow) => enabled && address !== null && isIdentityField(row),
    [enabled, address]
  );

  const commit = useCallback(
    (row: InlineRow, value: string | null) => {
      if (!enabled || address === null || !isIdentityField(row)) return;

      const nextDraft: IdentityDraft = { ...draft };
      if (value === served[row]) {
        // Back to what the server holds: not an edit.
        delete nextDraft[row];
      } else {
        nextDraft[row] = value;
      }
      setDraft(nextDraft);

      const keys = Object.keys(nextDraft) as IdentityField[];
      if (keys.length === 0) {
        // Never send `{}` — `params.require(:well)` would 400.
        debouncedSave.cancel();

        return;
      }

      debouncedSave(
        address,
        keys,
        Object.fromEntries(
          keys.map((field) => [
            IDENTITY_SERVER_KEY[field],
            nextDraft[field] ?? null,
          ])
        )
      );
    },
    // `served` is rebuilt every render; its content is what matters, and it is derived
    // from `well`.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the debounced saver is created once; re-creating it on every dep change would drop a pending save.
    [enabled, address, draft, well, debouncedSave]
  );

  return {
    values,
    pending: new Set(Object.keys(draft) as InlineRow[]),
    saving: editor.saving,
    error: editor.error,
    editable,
    commit,
    dismissError: editor.reset,
  };
};

// ── Add Wellbore preview (was use-wellbore-preview.ts) ──

/** Wait after the last change before asking the backend to re-derive. */
const PREVIEW_DEBOUNCE_MS = 400;

/** Query-key prefix for the Add Wellbore sheet's dry run. */
export const WELLBORE_PREVIEW_QUERY_KEY = 'wellbore-geometry:preview' as const;

export interface WellborePreviewResponse {
  /**
   * The trajectory the save would build. A GeoJSON LineString whose own keys are NOT
   * camelized — it is a plain hash on the backend, so it arrives as `type` / `coordinates`.
   */
  geometry: GeoJSON.LineString | null;
  /** Rounded to match what the save reports, so the two figures are comparable. */
  perforatedLateralLengthFt: number | null;
  /**
   * The sticks actually traversed, in trajectory order and deduped. This is how the sheet
   * learns the backend's same-stick collapse — both holes on one stick drops the rest —
   * without re-deriving it.
   */
  orderedMembers: number[];
  /** Over-long derived ties, sticks carrying no perforated length. De-duped by the backend. */
  warnings: string[];
  /**
   * Per non-surface stick, the one bottom-hole end the chain accepts for the current surface
   * hole + selection (a lone stick reports its locked end). Absent from older responses.
   */
  bottomHoleOptions?: BottomHoleOption[];
}

export interface WellborePreview extends WellborePreviewResponse {
  loading: boolean;
  /** Why these inputs would not build, in the backend's words. Only ever from a 422. */
  errors: string[];
  /**
   * The refusal's own code, for a caller that must route on WHICH refusal it was.
   *
   * The dry run shares its slot provider with the save, so a stick that has gone stale is
   * reported HERE first — the preview runs on every selection change, long before anyone
   * reaches for Save. A caller that only watched the save's refusal would act on it a
   * debounce too late, or never.
   */
  errorCode: string | null;
  /**
   * The slots a stale-stick refusal names — exactly the ones to drop.
   *
   * Null unless the backend named them, which is the difference between pruning the
   * selection precisely and guessing at it: a stick missing from a refreshed list may
   * simply be on another page of the current filter.
   */
  staleSlotIds: number[] | null;
}

const EMPTY: WellborePreview = {
  geometry: null,
  perforatedLateralLengthFt: null,
  orderedMembers: [],
  warnings: [],
  loading: false,
  errors: [],
  errorCode: null,
  staleSlotIds: null,
};

/** A 422's reasons, distinct from a failure that says nothing about the inputs. */
class PreviewRefused extends Error {
  // Fields, not constructor parameter properties: `erasableSyntaxOnly` allows only syntax
  // TypeScript can erase, and a parameter property emits an assignment.
  readonly reasons: string[];
  readonly code: string | null;
  readonly staleSlotIds: number[] | null;

  /**
   * @param {string[]} reasons The backend's own wording for the refusal.
   * @param {string | null} code Its code, where it gave one.
   * @param {number[] | null} staleSlotIds The slots a stale-stick refusal names.
   */
  constructor(
    reasons: string[],
    code: string | null,
    staleSlotIds: number[] | null
  ) {
    super('Preview refused');
    this.reasons = reasons;
    this.code = code;
    this.staleSlotIds = staleSlotIds;
  }
}

/**
 * Turns a failed request into either the dry run's reasons or a plain failure.
 *
 * The envelope is the app's standard `{ errors: [{ code, message, … }] }` — not the bare
 * `{ error }` the endpoint's summary suggested.
 * @param {unknown} error The thrown ky error.
 * @return {Promise<Error>} A `PreviewRefused` for a 422, else the original.
 */
const toPreviewError = async (error: unknown): Promise<Error> => {
  if (!(error instanceof HTTPError) || error.response.status !== 422) {
    return error instanceof Error ? error : new Error('Preview failed');
  }

  const body = (await error.response.json().catch(() => null)) as {
    errors?: {
      code?: string;
      message?: string;
      // Already camelCase in the Ruby hash — not jbuilder's doing, so not snake_case.
      details?: { staleSlotIds?: number[] };
    }[];
  } | null;

  const stale = body?.errors?.[0]?.details?.staleSlotIds;

  return new PreviewRefused(
    (body?.errors ?? [])
      .map((entry) => entry.message)
      .filter((message): message is string => !!message),
    body?.errors?.[0]?.code ?? null,
    Array.isArray(stale) && stale.length > 0 ? stale : null
  );
};

/**
 * Trails a key by the debounce.
 *
 * React Query has no debounce of its own, and checking a stick or moving a designation
 * changes the body, so the key it queries on trails the inputs.
 * @param {string | null} key The live key; null idles.
 * @return {string | null} The key once it has held still for the debounce.
 */
const useSettledKey = (key: string | null): string | null => {
  const [settledKey, setSettledKey] = useState<string | null>(key);
  useEffect(() => {
    if (key === null) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- the debounce that holds the preview key still; clearing it synchronously is what stops a request going out for a body the reader has already abandoned.
      setSettledKey(null);
      return undefined;
    }
    const timer = setTimeout(() => setSettledKey(key), PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [key]);
  return settledKey;
};

/**
 * Derives the wellbore the current selection and designations would build, via
 * `POST /wellbore_geometry_previews`.
 *
 * Compute-only, and it runs the SAME `LTDControlPointProvider` the save runs — so a clean
 * preview is the shape that will be built, and the sheet needs no geometry maths of its own.
 *
 * One caveat worth holding on to: the preview deliberately skips the surface-hole drilling
 * datum check, so a clean preview can still be refused on save with `NO_SURFACE_ELEVATION`.
 * The save's own error path stays load-bearing.
 * @param {WellboreBody | null} body The request body, or null to idle.
 * @return {WellborePreview} The previewed bore, its advisories, any refusals, and whether a
 *   call is out.
 */
export const useWellborePreview = (
  body: WellboreBody | null
): WellborePreview => {
  // Serialized so a change of VALUE re-derives, not a change of identity — the caller
  // rebuilds the body on every render.
  const key = body ? JSON.stringify(body) : null;

  const settledKey = useSettledKey(key);

  const { data, isFetching, error } = useQuery(
    queryOptions<WellborePreviewResponse, Error>({
      queryKey: [WELLBORE_PREVIEW_QUERY_KEY, settledKey],
      queryFn: async ({ signal }) => {
        try {
          return await api
            .post('wellbore_geometry_previews', {
              json: { wellbore: JSON.parse(settledKey!) as WellboreBody },
              signal,
            })
            .json<WellborePreviewResponse>();
        } catch (requestError) {
          throw await toPreviewError(requestError);
        }
      },
      enabled: settledKey !== null,
      // A dry run is only true of the inputs it was given, so nothing is kept past the
      // answer, and a refusal must not be retried into a delayed pass.
      // v5 spelling of cacheTime.
      gcTime: 0,
      retry: false,
    })
  );

  if (settledKey === null) return EMPTY;

  // Only a 422 says anything about the inputs. A network failure or a 500 says something
  // about the connection, and reporting that as "your selection is invalid" would both lie
  // and hold a save that would have worked.
  if (error) {
    return {
      ...EMPTY,
      errors: error instanceof PreviewRefused ? error.reasons : [],
      errorCode: error instanceof PreviewRefused ? error.code : null,
      staleSlotIds: error instanceof PreviewRefused ? error.staleSlotIds : null,
    };
  }

  return {
    geometry: data?.geometry ?? null,
    perforatedLateralLengthFt: data?.perforatedLateralLengthFt ?? null,
    orderedMembers: data?.orderedMembers ?? [],
    warnings: data?.warnings ?? [],
    loading: isFetching,
    errors: [],
    errorCode: null,
    staleSlotIds: null,
  };
};

/** Query-key prefix for the bottom-hole options request. */
export const BOTTOM_HOLE_OPTIONS_QUERY_KEY =
  'wellbore-geometry:bottom-hole-options' as const;

/** What the bottom-hole options request knows. */
export interface BottomHoleOptionsState {
  /** Valid bottom end per slot id; null while unknown (idle, in flight, or failed). */
  validEnds: Map<number, LtdEndKey> | null;
  /**
   * Whether the request has an ANSWER for the inputs on screen — success or failure — or has
   * nothing to ask (idle). False from the moment the inputs change until it settles, which is
   * what lets a dependent request wait rather than fire on a guessed bottom hole.
   */
  resolved: boolean;
}

/**
 * Which bottom-hole end is valid for each selected stick, via the same
 * `POST /wellbore_geometry_previews` with the bottom hole OMITTED.
 *
 * A separate request from `useWellborePreview` on purpose: it keys on the selection and the
 * surface hole only, so choosing a bottom hole does not refetch it, and — because the full
 * preview 422s on an inconsistent bottom hole and so returns no options — the options stay
 * available exactly when the reader needs them to correct that pick.
 *
 * `validEnds` is null while unknown (idle, in flight, or any failure): the greying is
 * guidance, and the backend still refuses an inconsistent pick on its own. `resolved` tells a
 * caller whether "null" means "still asking" (false) or "asked, no usable answer" (true).
 * @param {WellboreSurfaceBody | null} body The selection + surface hole, or null to idle.
 * @return {BottomHoleOptionsState} Valid bottom end per slot id, and whether it has settled.
 */
export const useBottomHoleOptions = (
  body: WellboreSurfaceBody | null
): BottomHoleOptionsState => {
  const key = body ? JSON.stringify(body) : null;
  const settledKey = useSettledKey(key);

  const { data, isLoading } = useQuery(
    queryOptions<BottomHoleOption[], Error>({
      queryKey: [BOTTOM_HOLE_OPTIONS_QUERY_KEY, settledKey],
      queryFn: async ({ signal }) => {
        const response = await api
          .post('wellbore_geometry_previews', {
            json: { wellbore: JSON.parse(settledKey!) as WellboreSurfaceBody },
            signal,
          })
          .json<WellborePreviewResponse>();
        return response.bottomHoleOptions ?? [];
      },
      enabled: settledKey !== null,
      // v5 spelling of cacheTime.
      gcTime: 0,
      retry: false,
    })
  );

  // Only trust an answer for the inputs on screen; while the debounce is pending the
  // previous selection's answer is not true of this one.
  const current = key !== null && settledKey === key;

  return useMemo(
    () => ({
      validEnds:
        data && current
          ? new Map(data.map((option) => [option.slotId, option.validEnd]))
          : null,
      // Nothing to ask is settled; otherwise settled once the debounce has caught up AND
      // the request is no longer loading (a refusal counts — it ends the wait).
      resolved: key === null || (current && !isLoading),
    }),
    [data, current, isLoading, key]
  );
};

/**
 * Re-reads the LTD catalog, for when the backend reports the picked sticks have moved on.
 *
 * Here rather than in the sheet because the sheet is a component, and components call the
 * feature's hooks rather than React Query directly.
 * @return {() => void} Invalidates every LTD candidate page.
 */
export const useRefreshLtdCandidates = () => {
  const queryClient = useQueryClient();

  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ltdCandidateKeys.all });
  }, [queryClient]);
};
